'use client';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Link from '@/components/LocaleLink';
import dynamic from 'next/dynamic';
import {
  //  usePathname,
  useRouter,
} from 'next/navigation';
import { Modal } from '@/components/reactstrap';
import Skeleton from '@mui/material/Skeleton';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import RangeSlider from '@/components/RangeSlider';
import StyledDatePicker from '@/components/StyledDatePicker';
import dayjs from 'dayjs';
// import ChartComponent from '@/components/ChartComponent';
import { lifemeasureColors } from '@/constant';
import { seo, hashOfLMMetaData } from '@/utils/seo';
import { getFamilyMembers, getMeasurementsOfMember, getUserProfile, getAnonymousSessionId } from '@/utils/user_service';
import {
  calculateAgeFromBob,
  FormulateChartURLs,
  calculateAgeInMonthsWithTwoDates,
  getAge,
  convertAllDataPointsByUnit,
  // capitalizeFirstLetter,
  getDateBeforeDays,
  calculateBMI,
  convertCmToFeetInch,
  getJSON_LD_infoURL,
  getChartType,
  formatNumber,
} from '@/utils/lm_utilities';
import { formulateChartUrl, getChartImageAltValue, getFileName } from '@/utils/intermediary_url_service';
import { parseUrlForGenderWithString } from '@/utils/maps';
import _ceil from 'lodash/ceil';
import _round from 'lodash/round';
import _floor from 'lodash/floor';
import _first from 'lodash/first';
import { base64ToBlob, getAgeFloorAndCeil, setInitialState } from '@/utils/charts_utils';
import { getAgeDescAdjective } from '@/utils/lm_title_description_service';
import {
  // addChartLogData,
  getChartDataSetByGenderAndAge,
  getChartLogByDate,
  uploadImage,
} from '@/utils/chart_service';
import useLocalePath from '@/hooks/useLocalePath';
import { IdleCalulateText } from './PageServerSideData';
import { capitalizeChartName, fetchPercentileData, formatDate } from '@/utils/baseChart_utils';
import RadioGroup from './HoseholdRadioCompoenent';
import safeWindow from '@/utils/safeWindow';
import { useTranslation } from '@/contexts/TranslationContext';
import { formatCurrency } from '@/utils/formatLocale';

const GenderSelect = dynamic(() => import('@/components/GenderSelect'), {
  ssr: false,
});
const FamilyMemberForm = dynamic(() => import('@/components/familyMemberForm'), { ssr: false });
const SaveModal = dynamic(() => import('@/components/SaveModal'), { ssr: false });
const ChartSpeedDial = dynamic(() => import('@/components/speedDial'), { ssr: false });
const ChartComponent = dynamic(() => import('@/components/ChartComponent'), {
  ssr: false,
  loading: () => <Skeleton variant="rectangular" width={'100%'} height={400} className="skeleton-bg" />,
});

const OTHER_LINK_CHART_TYPE_KEY = {
  sidebarNet_WorthChartUrl: 'chartType.netWorth',
  sidebarAnnual_IncomeChartUrl: 'chartType.annualIncome',
  sidebarRetirement_FundChartUrl: 'chartType.retirementFund',
};

const BaseChart = ({
  params,
  metaInfo,
  pageTitleDetails,
  chartType,
  measurementType,
  chartLinkNmae,
  chartTitle,
  chartSubtitle,
  unitOptions,
  measurementConfig,
  handleMeasurement,
  calculateMeasurement,
  getDataPoints,
  getAxisPoints,
  calculatePercentile,
  blurbData,
  children,
  ...props
}) => {
  const session = useSessionContext();
  const localePath = useLocalePath();
  const { t, locale } = useTranslation();
  const { id } = params;
  const initialPathname = metaInfo?.initialPathname;

  // States
  const [showFmModal, setShowFmModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [hashArray, setHashArray] = useState(initialPathname.split('/'));
  const [familyMembersList, setFamilyMembersList] = useState([]);
  const [chartOpt, setCharOpt] = useState({});
  const [scatterChartOpt, setScatterCharOpt] = useState({});
  const [validations, setValidations] = useState(null);
  const [viewChartDisable, setViewChartDisable] = useState(false);
  const [renderAdSlot, setRenderAdSlot] = useState(false);
  const [localStorageData, setLocalStorageData] = useState('');
  const [otherLinks, setOtherLinks] = useState({});
  const [chart, setChart] = useState({});
  const [chartValue, setChartValue] = useState('');
  const [isChartShow, setIsChartShow] = useState(false);
  const [isNewChart, setIsNewChart] = useState(false);
  const [isValueChange, setIsValueChange] = useState(false);
  const [zoomedIn, setZoomedIn] = useState(true);
  const [chartImage, setChartImage] = useState(null);
  const [shouldUpdate, setShouldUpdate] = useState(true);
  const [scatterData, setScatterData] = useState([]);
  const [userProfile, setUserProfile] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [percentileData, setPercentileData] = useState(false);
  const [generateNewImage, setGenerateNewImage] = useState(false);
  const [showClientComponent, setShowClientComponent] = useState(false);
  // const [defautlValueUpdated, setDefaultValuesUpdated] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [prevPathname, setPrevPathname] = useState(null);
  const [useDates, setUseDates] = useState(Boolean(safeWindow?.sessionStorage.getItem('useDates')));
  const [anonymousSessionId, setAnonymousSessionId] = useState(null);

  const router = useRouter();
  // Refs
  const chartRef = useRef(null);
  const chartExportRef = useRef(null);
  const chartScrollRef = useRef(null);
  const uomRef = useRef(null);
  const [chartUpdateTrigger, setChartUpdateTrigger] = useState(0);

  const initialFormData = useMemo(
    () => setInitialState(initialPathname, localStorageData, measurementType),
    [initialPathname, localStorageData, measurementType],
  );

  const validUnits = ['kg', 'kgs', 'lb', 'lbs', 'pounds', 'cms', 'inches', 'cms-kgs', 'inches-lbs'];
  useEffect(() => {
    // if (typeof window !== 'undefined') {
    setPrevPathname(safeWindow.sessionStorage.getItem('initialPathname'));
    setUseDates(safeWindow.sessionStorage.getItem('useDates') === 'true');
    // }
  }, []);

  // const useDates = typeof window !== 'undefined' && window?.sessionStorage.getItem('useDates') === 'true';

  // STEP 1: Resolve the base DOB and MD using original logic
  const baseDobRaw =
    typeof window !== 'undefined' && prevPathname === initialPathname
      ? safeWindow.sessionStorage.getItem('dob') || initialFormData.dob
      : initialFormData.dob;

  const baseMdRaw =
    typeof window !== 'undefined' && prevPathname === initialPathname
      ? safeWindow.sessionStorage.getItem('md') || initialFormData.md
      : initialFormData.md;

  // STEP 2: Convert base to Date objects
  const dobBase = new Date(baseDobRaw);
  const mdBase = new Date(baseMdRaw);

  // STEP 3: Adjust day if useDates is true
  let updated_DOB = dobBase;
  let updated_MD = mdBase;

  if (useDates) {
    const storedDob = safeWindow.sessionStorage.getItem('dob');
    const storedMd = safeWindow.sessionStorage.getItem('md');

    if (storedDob) {
      const storedDobDate = new Date(storedDob);
      updated_DOB = new Date(dobBase.getFullYear(), dobBase.getMonth(), storedDobDate.getDate());
    }

    if (storedMd) {
      const storedMdDate = new Date(storedMd);
      updated_MD = new Date(mdBase.getFullYear(), mdBase.getMonth(), storedMdDate.getDate());
    }
  }
  // console.log(updated_DOB, "check dates");
  // console.log(dobBase, "check dates dobBase");
  // console.log(safeWindow.sessionStorage.getItem('dob'), "check dates in basechart");
  
  const [formData, setFormData] = useState(() => ({
    ...initialFormData,
    unit: params.unit || initialFormData.unit,
   dob: formatDate(updated_DOB),
    md: formatDate(updated_MD),
    sliderValue: 50,
    member_id: safeWindow.sessionStorage.getItem('member_id') || null,
    member_name: safeWindow?.sessionStorage.getItem('member_name') || null,
  }));
  const calculated_age_slider = useMemo(
    () => _ceil(calculateAgeInMonthsWithTwoDates(formData.dob, formData.md)),
    [formData.dob, formData.md],
  );
  const calculated_age_slider_in_years = parseInt(calculated_age_slider / 12);
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      await fetchPercentileData({
        calculated_age_slider: calculated_age_slider_in_years,
        chartType: measurementType,
        formData,
        isMounted,
        setFormData,
        setPercentileData,
        // setDefaultValuesUpdated,
        getChartDataSetByGenderAndAge,
      });
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [chartType]);

  useEffect(() => {
    if (prevPathname === initialPathname && uomRef.current && !viewChartDisable && !isValueChange) {
      setTimeout(() => {
        uomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [initialPathname, prevPathname]);

  // let type;
  // if (measurementType === 'height') {
  //   type = 'Length';
  // } else {
  //   type = capitalizeFirstLetter(measurementType);
  // }
  const type = useMemo(() => {
    return capitalizeChartName(measurementType);
  }, [measurementType]);

  const localizedChartType = useMemo(() => {
    return getChartType(hashArray, locale);
  }, [hashArray, locale]);

  const measurementSeriesName = t('baseChart.chartTypeMeasurement', { chartType: localizedChartType });

  // const [result, setResult] = useState({
  //   age: 0,
  //   percentile: 0,
  //   type: `${type}Measurement`,
  //   value: initialFormData ? initialFormData.l_measurement : 0,
  //   measure_date: initialFormData ? initialFormData.md : '',
  //   family_member_id:
  //     formData.member_id || (typeof window !== 'undefined' && Number(window.sessionStorage.getItem('member_id'))),
  // });
  const [result, setResult] = useState(() => {
    const memberId = formData.member_id || safeWindow.sessionStorage.getItem('member_id');

    return {
      age: 0,
      percentile: 0,
      type: `${type}Measurement`,
      value: initialFormData?.l_measurement || 0,
      measure_date: initialFormData?.md || '',
      family_member_id: memberId,
    };
  });

  // const fileName = getFileName(blurbData, hashArray, initialPathname);

  // const chartImageAlt = getChartImageAltValue(blurbData, hashArray, initialPathname);

  const fileName = useMemo(
    () => getFileName(blurbData, hashArray, initialPathname, null, locale),
    [blurbData, hashArray, initialPathname, locale],
  );

  const chartImageAlt = useMemo(
    () => getChartImageAltValue(blurbData, hashArray, initialPathname, null, locale),
    [blurbData, hashArray, initialPathname, locale],
  );

  const handleAddFamButton = useCallback((e) => {
    e.preventDefault();
    setShowFmModal(true);
  }, []);

  const handleClose = () => {
    setShowFmModal(false);
    safeWindow.location.reload();
  };

  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
  };

  const handleShowSaveModal = () => {
    setShowSaveModal(true);
  };

  const scrollToChart = () => {
    if (chartScrollRef.current) {
      chartScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const getCurrentUserProfile = () => {
    getUserProfile()
      .then((res) => {
        if (res.status === 200) {
          return res.json();
        }
        return res.json();
      })
      .then((data) => {
        setUserProfile(data);
      })
      .catch((err) => console.log(err));
  };

  // const getMembersList = async () => {
  //   const memberId = typeof window !== 'undefined' && Number(window.sessionStorage.getItem('member_id'));
  //   if (memberId) {
  //     setFormData({
  //       ...formData,
  //       member_id: memberId,
  //     });

  //     try {
  //       const res = await getMeasurementsOfMember(memberId);

  //       // if (res.status === 401 || res.status === 403) {
  //       //   window.localStorage.setItem('lm_previous_path', window.location.pathname);
  //       //   // window.location.href = '/signin';
  //       //   // return;
  //       // }

  //       const data = await res.json();

  //       const measurementTypeMap = {
  //         NetWorthChart: 'NetWorthMeasurement',
  //         AnnualIncomeChart: 'AnnualIncomeMeasurement',
  //         RetirementFundChart: 'RetirementFundMeasurement',
  //         // WeightChart: 'WeightMeasurement',
  //       };

  //       const measurementKey = measurementTypeMap[chartType];
  //       const rawMeasurements = data.family_member_data[measurementKey];

  //       let latestMeasurement = null;

  //       if (
  //         Array.isArray(rawMeasurements) &&
  //         rawMeasurements.length > 0 &&
  //         Array.isArray(rawMeasurements[0]) &&
  //         rawMeasurements[0].length > 0
  //       ) {
  //         //Sort the first sub-array by updated_at descending and pick the latest
  //         const sorted = rawMeasurements[0].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  //         latestMeasurement = sorted[0]; // Most recent measurement
  //       }

  //       if (latestMeasurement) {
  //         if (chartType === 'WeightChart') {
  //           const finalWeight =
  //             params.unit === 'kgs' ? latestMeasurement.value : _round(latestMeasurement.value * 2.20462);
  //           setFormData({
  //             ...formData,
  //             l_measurement: finalWeight,
  //             member_id: memberId,
  //           });
  //         } else if (chartType === 'LengthChart') {
  //           let finalLength = 0;

  //           if (formData.unit === 'cms') {
  //             finalLength = latestMeasurement.value;
  //           } else if (formData.unit === 'inches') {
  //             finalLength = _round(latestMeasurement.value / 2.54); // cm → inches
  //           } else if (formData.unit === 'feet') {
  //             finalLength = _floor(latestMeasurement.value / 30.48); // cm → feet (rounded to whole feet)
  //           }

  //           setFormData({
  //             ...formData,
  //             l_measurement: finalLength,
  //             l_height_ft: formData.unit === 'feet' ? finalLength : formData.l_height_ft,
  //             member_id: memberId,
  //           });
  //         }
  //       } else {
  //         console.log('No measurement data found for this type.');
  //       }
  //     } catch (error) {
  //       console.error('watch Failed to fetch measurements:', error);
  //     }
  //   }

  //   getFamilyMembers()
  //     .then((res) => {
  //       if (res.status === 200) {
  //         return res.json();
  //       } else if (res.status === 401 || res.status === 403) {
  //         // window.localStorage.setItem('lm_previous_path', window.location.pathname);
  //         // window.location.href = '/signin';
  //       }
  //       return res.json();
  //     })
  //     .then((data) => {
  //       setFamilyMembersList(data);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // };

  const getMembersList = async () => {
    const memberId = safeWindow.sessionStorage.getItem('member_id');
    if (memberId) {
      setFormData({
        ...formData,
        member_id: memberId,
      });

      try {
        const res = await getMeasurementsOfMember(memberId);
        const data = await res.json();

        const measurementTypeMap = {
          NetWorthChart: 'NetWorthMeasurement',
          AnnualIncomeChart: 'AnnualIncomeMeasurement',
          RetirementFundChart: 'RetirementFundMeasurement',
        };

        const measurementKey = measurementTypeMap[chartType];
        const rawMeasurements = data.family_member_data[measurementKey];

        let latestMeasurement = null;

        if (
          Array.isArray(rawMeasurements) &&
          rawMeasurements.length > 0 &&
          Array.isArray(rawMeasurements[0]) &&
          rawMeasurements[0].length > 0
        ) {
          // Sort the first sub-array by updated_at descending and pick the latest
          const sorted = rawMeasurements[0].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
          latestMeasurement = sorted[0]; // Most recent measurement
        }

        if (latestMeasurement) {
          // All new chart types use currency in USD
          if (['NetWorthChart', 'AnnualIncomeChart', 'RetirementFundChart'].includes(chartType)) {
            setFormData({
              ...formData,
              l_measurement: latestMeasurement.value,
              l_measurement_unit: 'USD',
              member_id: memberId,
            });
          }
        } else {
          console.log('No measurement data found for this type.');
        }
      } catch (error) {
        console.error('Failed to fetch measurements:', error);
      }
    }

    getFamilyMembers()
      .then((res) => {
        if (res.status === 200) {
          return res.json();
        } else if (res.status === 401 || res.status === 403) {
          // window.localStorage.setItem('lm_previous_path', window.location.pathname);
          // window.location.href = '/signin';
        }
        return res.json();
      })
      .then((data) => {
        setFamilyMembersList(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleFamilyMemberChange = async (e) => {
    setIsNewChart(true);
    setIsValueChange(true);

    const selectedId = e.target.value;
    const member = familyMembersList.find((m) => m.id == selectedId);

    if (member) {
      const dob = new Date(member.dob).toLocaleDateString('en-CA');

      setFormData({
        ...formData,
        dob: dob,
        gender: member.gender,
        member_id: member.id,
        member_name: member.name,
      });

      setResult({
        ...result,
        family_member_id: member.id,
      });
      let calculated_age = _ceil(calculateAgeInMonthsWithTwoDates(dob, formData.md));
      let url = formulateChartUrl(formData, chartLinkNmae, calculated_age, params.unit);

      if (safeWindow.location.pathname !== url) {
        safeWindow.sessionStorage.setItem('member_id', member.id);
        safeWindow.sessionStorage.setItem('member_name', member.name);
        safeWindow.sessionStorage.setItem('useDates', true);
        // safeWindow.location.href = url;
      }
    } else {
      safeWindow.sessionStorage.setItem('member_id', null);
    }
  };

  const changeSliderValue = (newSliderValue) => {
    setFormData((prev) => ({
      ...prev,
      sliderValue: newSliderValue,
    }));
  };
  const handleInputChange = (event, _value = undefined) => {
    setIsValueChange(true);

    if (!isNewChart) {
      setIsNewChart(true);
    }

    const target = event.target || {}; // in case MUI passes a synthetic event object
    const name = target.name;

    // Decide value
    const value = target.type === 'checkbox' ? target.checked : _value !== undefined ? _value : target.value;

    if (viewChartDisable === true) {
      setViewChartDisable(false);
    }

    if (isDefault) {
      setIsDefault(false);
    }

    if (['dob', 'md'].includes(name)) {
      safeWindow.sessionStorage.setItem(name, value);
      safeWindow.sessionStorage.setItem('useDates', true);
      safeWindow.sessionStorage.setItem('initialPathname', initialPathname);
    }

    if (name === 'dob') {
      validateDoB(value);
      if (validateDoB(value)) {
        setViewChartDisable(false);
        setValidations(null);
      } else {
        setViewChartDisable(true);
      }
    }

    let updatedFormData = {
      ...formData,
      [name]: value,
    };

    setFormData(updatedFormData);
  };

  // Handle date change for DatePicker
  const handleDateChange = (newValue) => {
    const value = newValue ? newValue.format('YYYY-MM-DD') : '';
    handleInputChange({
      target: { name: 'dob', value: value },
    });

  };

  const validateDoB = (dob) => {
    let isValid = true;
    let validationMsg;
    if (!dob) {
      validationMsg = t('baseChart.validation.selectDob');
      isValid = false;
    }
    if (dob && calculateAgeFromBob(dob) > 100) {
      validationMsg = t('baseChart.validation.ageRange');
      isValid = false;
    }
    if (!isValid) {
      setValidations(validationMsg);
    }
    return isValid;
  };

  const calculateURLs = async (temp_age, pathname) => {
    let currentPath = pathname;
    if (params.unit && validUnits.includes(params.unit.toLowerCase())) {
      const segments = currentPath.split('/');
      segments.pop();
      currentPath = segments.join('/');
    }
    setOtherLinks(FormulateChartURLs(currentPath));
  };
  const updateChart = async () => {
    try {
      // const temp_age = _ceil(calculateAgeInMonthsWithTwoDates(formData.dob, formData.md));

      let currentPath = safeWindow.location.pathname;

      calculateURLs(calculated_age_slider, currentPath);

      const originalMeasurement = Number(metaInfo.defaultMeasurementValue).toFixed(2);

      const values = calculateMeasurement?.(formData, originalMeasurement, !isNewChart, params.unit);

      if (!isNewChart || measurementType === 'bmi') {
        setFormData({ ...formData, ...values });
      }

      let defaultMeasurement = isNewChart && isValueChange ? values.l_measurement : originalMeasurement;

      if (measurementType === 'bmi') defaultMeasurement = values.bmi;

      setChartValue(defaultMeasurement);

      const percentileData = await calculatePercentile(defaultMeasurement, formData, isNewChart);

      if (measurementType === 'bmi' || (percentileData && isValueChange)) {
        const measurementValue = measurementType === 'bmi' ? values.bmi : values.l_measurement;

        // Update result state with the new values
        setResult((prev) => ({
          ...prev,
          age: calculated_age_slider,
          gender: formData.gender,
          percentile: percentileData.percentile,
          value: measurementValue,
        }));

        // Use the values directly instead of relying on state
        await loadChartForm(measurementValue, percentileData.percentile);
      } else {
        const measurementValue = measurementType === 'bmi' ? values.bmi : values.l_measurement;
        setResult((prev) => ({
          ...prev,
          age: calculated_age_slider,
          gender: formData.gender,
          percentile: 50,
          value: defaultMeasurement,
        }));
        await loadChartForm(defaultMeasurement, percentileData.percentile);
      }
    } catch (err) {
      console.error('Error updating chart:', err);
    }
  };

  const fetchScatterChartData = async () => {
    const currentDate = getDateBeforeDays(30);
    let type = measurementType;
    // if (type === 'Height') type = 'Length';

    // const calculated_age = calculateAgeInMonthsWithTwoDates(formData.dob, formData.md);

    try {
      const response = await getChartLogByDate(
        currentDate,
        type,
        formData.gender,
        calculated_age_slider,
        formData.unit,
      );
      setScatterData(response?.data || []);
    } catch (error) {
      console.error('Error fetching scatter chart data:', error);
      setScatterData([]); // fallback to avoid UI breakage
    }
  };

  useEffect(() => {
    if (blurbData.ageDesc && scatterData && scatterData?.length > 0) {
      setScatterCharOpt(drawScatterChartByOptions(scatterData));
    }
  }, [blurbData.ageDesc, scatterData?.length, updateCount]);
  //! add count here

  const addChartlogData = async (data) => {
    const typeMap = {
      // Height: 'Length',
      // Bmi: 'Bmi',
      // Head: 'Head',
      // Weight: 'Weight',
      // TODO // CODE TO REMOVE ABOVE
      'net-worth': 'net-worth',
      Income: 'Income',
      'Retirement-fund': 'Retirement-Fund',
    };

    const rawType = capitalizeFirstLetter(measurementType);
    const type = typeMap[rawType] || rawType;

    // const calculated_age = calculateAgeInMonthsWithTwoDates(formData.dob, formData.md);

    const obj = {
      ...data, // add everything from `data` first
      chart_type: `${type}`,
      gender: data.gender,
      measurement_unit: data.unit,
      date_measured: data.md,
      // TODO // AGE_CONVERSION
      age: parseInt(calculated_age_slider / 12),
    };

    switch (type) {
      case 'Length':
        obj.height = data.l_measurement;
        break;
      case 'Bmi':
        obj.height = data.l_measurement;
        obj.weight = data.w_measurement;
        break;
      case 'Head':
        obj.head_circumference = data.l_measurement;
        break;
      case 'Weight':
        obj.weight = data.l_measurement;
        break;
    }

    // await addChartLogData(obj);
  };

  useEffect(() => {
    if (shouldUpdate) {
      updateChart();
      setShouldUpdate(false);
    }
  }, [shouldUpdate]);

  const loadChartForm = async (measurement, activePercentile) => {
    // const calculated_age = calculateAgeInMonthsWithTwoDates(formData.dob, formData.md);

    try {
      const data = await getDataPoints(formData.gender, calculated_age_slider, formData.unit);
      let options = getAxisPoints(formData.unit, calculated_age_slider);
      options.yAxisTitle = t('baseChart.yAxisTitle', {
        chartTitle: localizedChartType,
        unit: formData.unit === 'inches' ? 'inches' : formData.unit,
        ageDesc: getAgeDescAdjective(initialPathname, locale),
      });
      options.xAxisTitle = hashOfLMMetaData.pageH2Title;
      options.activePercentile = activePercentile;
      let x_axis_data_point = calculated_age_slider;
      if (calculated_age_slider >= 25) {
        x_axis_data_point = calculated_age_slider - 24;
      }
      options.calculated_age = calculated_age_slider;

      let dataset = {
        name: measurementSeriesName,
        marker: { enabled: true, symbol: 'circle' },
        color: '#25AF9B',
        data: [{ x: x_axis_data_point, y: measurement }],
      };

      let resultSet = convertAllDataPointsByUnit(formData.unit, data.charts);

      resultSet.push(dataset);

      setCharOpt(drawChartByOptions(resultSet, options));

      // Update result state with the final values
      setResult((prev) => ({
        ...prev,
        age: calculated_age_slider,
        gender: formData.gender,
        percentile: activePercentile,
        value: measurement,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const captureChartAsImage = async () => {
    const chartContainer = document.getElementById(`${measurementType}_chart`);

    if (!chartContainer) {
      console.error('Chart container not found.');
      return;
    }

    const html2canvas = (await import('html2canvas')).default;
    html2canvas(chartContainer)
      .then(async (canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const blob = base64ToBlob(imgData, 'image/png');

        // const today = new Date();
        const datedFileName = getFileName(blurbData, hashArray, initialPathname, null, locale);
        const file = new File([blob], datedFileName + '.png', { type: 'image/png' });

        try {
          const res = await uploadImage(file);
          const isValidCloudinaryUrl = typeof res === 'string' && res.startsWith('https://res.cloudinary.com/');
          if (isValidCloudinaryUrl) {
            setChartImage(res);
            setShowClientComponent(true);
            const seoDescriptionDiv = document.getElementById('initial-page-description');
            if (seoDescriptionDiv) seoDescriptionDiv.remove();
          } else {
            setChartImage(imgData);
            setShowClientComponent(true);
            const seoDescriptionDiv = document.getElementById('initial-page-description');
            if (seoDescriptionDiv) seoDescriptionDiv.remove();
          }
        } catch (err) {
          setChartImage(imgData);
          setShowClientComponent(true);
          const seoDescriptionDiv = document.getElementById('initial-page-description');
          if (seoDescriptionDiv) seoDescriptionDiv.remove();
        }
      })
      .catch((err) => {
        console.log('count Error capturing chart as image:', err);
      });
  };

  const drawChartByOptions = (resultSet, options) => {
    // const activePercentile = _round(options.activePercentile);
    const activePercentile = _floor(options.activePercentile);
    return {
      chart: {
        type: 'column',
        backgroundColor: '#FFFFFF',
        spacing: [10, 5, 10, 5],
        height: '400px',
        events: {
          render: () => {
            setTimeout(() => {
              checkImageExists(false);
            }, 1000);
          },
        },
      },
      title: {
        text: options.xAxisTitle,
        style: {
          color: '#059669',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      },
      xAxis: {
        categories: resultSet.filter((d) => d.name !== measurementSeriesName).map((d) => `P${d.name}`),
        title: {
          text: t('baseChart.percentile'),
          style: { color: '#059669' },
        },
        labels: {
          step: 5,
          rotation: -45,
          style: { fontSize: '10px' },
        },
      },
      yAxis: {
        title: {
          text: options.yAxisTitle,
          style: { color: '#059669' },
        },
        min: 0,
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          groupPadding: 0,
          borderWidth: 0,
          shadow: false,
        },
      },
      series: [
        {
          name: localizedChartType,
          type: 'column',
          data: resultSet
            .filter((d) => d.name !== measurementSeriesName)
            .map((d) => ({
              y: parseFloat(d.data.toFixed(1)),
              percentile: d.name,
              color:
                d.name === activePercentile
                  ? lifemeasureColors.active
                  : d.name < activePercentile
                  ? lifemeasureColors.below
                  : lifemeasureColors.above,
            })),
          showInLegend: false,
        },
      ],
      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
    };
  };

  let subtitle = null;

  function capitalizeFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  const drawScatterChartByOptions = (resultSet) => {
    // const activePercentile = _round(options.activePercentile);
    let title = t('baseChart.priorMeasurements', {
      gender: parseUrlForGenderWithString(hashArray, locale),
      ageDesc: getAgeDescAdjective(initialPathname, locale),
    });

    let series = [
      {
        name: t('baseChart.priorPlots'),
        color: '#25AF9B',
        data: resultSet,
      },
    ];
    let yaxisPlot = [];
    if (isChartShow) {
      let activePoint;

      if (measurementType != 'bmi') {
        activePoint = formData.l_measurement;
      } else {
        activePoint = calculateBMI(
          formData.w_measurement,
          formData.l_measurement,
          formData.unit === 'cms-kgs' ? 'metric' : 'imperial',
        );
      }
      const maxXValue = Math.max(...resultSet?.map((point) => point[0]));
      const maxYValue = Math.max(...resultSet?.map((point) => point[1]));

      let yaxisPlotLabel;
      let yaxisPlotShorterLabel;
      if (measurementType == 'bmi') {
        yaxisPlotLabel = `Your BMI is lower than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
        yaxisPlotShorterLabel = `Your BMI is higher than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
      } else if (measurementType == 'height') {
        yaxisPlotLabel = `You are taller than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
        yaxisPlotShorterLabel = `You are shorter than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
      } else if (measurementType == 'weight') {
        yaxisPlotLabel = `You are heavier than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
        yaxisPlotShorterLabel = `You are lighter than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
      } else if (measurementType == 'head') {
        yaxisPlotLabel = `You are head greater than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
        yaxisPlotShorterLabel = `You are head shorter than these ${parseUrlForGenderWithString(hashArray, locale).toLowerCase()}`;
      }
      yaxisPlot = [
        {
          from: 0,
          to: activePoint,
          color: '#7BCCAD',
          label: {
            text: yaxisPlotLabel,
            align: 'center',
            verticalAlign: 'bottom',
            y: -15,
          },
        },
        {
          from: activePoint,
          to: maxYValue + 100,
          color: '#5FEF5F',
          label: {
            text: yaxisPlotShorterLabel,
            align: 'center',
            verticalAlign: 'top',
            y: 15,
          },
        },
      ];

      series = [
        {
          name: parseUrlForGenderWithString(hashArray, locale),
          // name: 'Prior plots on Nettleworth.com',
          color: '#25AF9B',
          data: resultSet,
          cluster: {
            enabled: true,
            allowOverlap: false,
            animation: {
              duration: 450,
            },
            layoutAlgorithm: {
              type: 'kmeans',
              distance: 20,
            },
            zones: [
              {
                from: 1,
                to: 4,
                marker: {
                  radius: 4,
                },
              },
              {
                from: 5,
                to: 9,
                marker: {
                  radius: 6,
                },
              },
              {
                from: 10,
                marker: {
                  radius: 8,
                },
              },
            ],
          },
        },
        {
          name: t('baseChart.scatterYourLine', { chartType: localizedChartType }),
          type: 'line',
          color: '#176C5A',
          dashStyle: 'dash',
          data: [
            [0, activePoint],
            [maxXValue, activePoint],
          ],
          lineWidth: 2,
          zIndex: 1,
        },
      ];
    }

    return {
      chart: {
        type: 'scatter',
        zoomType: 'xy',
      },
      title: {
        text: !isChartShow ? title : null,
      },
      subtitle: {
        text: null,
      },
      xAxis: {
        title: {
          text: isChartShow ? t('baseChart.scatterXAxisIndex') : t('baseChart.scatterXAxisValues'),
        },
      },
      yAxis: {
        title: {
          text: t('baseChart.scatterYAxis', { type: localizedChartType, unit: formData.unit }),
        },

        plotBands: yaxisPlot,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 600,
            },
            chartOptions: {
              chart: {
                spacing: [10, 0, 10, 0],
              },
              yAxis: {
                title: {
                  text: t('baseChart.scatterYAxis', { type: localizedChartType, unit: formData.unit }),
                },
                labels: {
                  enabled: isChartShow ? false : true,
                },
              },
            },
          },
        ],
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 2.5,
          },
        },
      },
      series: series,
      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
    };
  };

  const scatterChartOptions = useMemo(() => {
    if (!isChartShow) return null;
    return drawScatterChartByOptions(scatterData);
  }, [isChartShow, chartUpdateTrigger]);

  useEffect(() => {
    if (scatterChartOptions) setScatterCharOpt(scatterChartOptions);
  }, [scatterChartOptions]);

  const newChart = (event, scroll = true) => {
    setUpdateCount((prev) => prev + 1);
    if (event) event.preventDefault();
    setIsValueChange(true);
    setChartUpdateTrigger((prev) => prev + 1);

    safeWindow.localStorage.setItem(`lm_${measurementType}_data`, JSON.stringify(formData));

    let calculated_age = _ceil(calculateAgeInMonthsWithTwoDates(formData.dob, formData.md));
    let url = localePath(formulateChartUrl(formData, chartLinkNmae, calculated_age, params.unit));

    if (safeWindow.location.pathname !== url) {
      safeWindow.sessionStorage.setItem('useDates', true);
      safeWindow.location.href = url;
    }
    // console.log(formData, 'check formData');

    addChartlogData(formData);
    setIsChartShow(true);
    if (isNewChart) {
      updateChart();
    } else {
      loadChartForm(chartValue, result?.percentile);
    }
    setIsNewChart(true);
    if (scroll) {
      setTimeout(() => {
        scrollToChart();
      }, 100);
    }

    setViewChartDisable(true);
  };
  useEffect(() => {
    if (session.doesSessionExist) {
      getCurrentUserProfile();
      getMembersList();
    }
  }, [session]);

  useEffect(() => {
    seo(safeWindow.location.pathname);
    setHashArray(initialPathname.split('/'));
    // setRenderAdSlot(safeWindow.innerWidth > 1000);
    // TODO // ads remove
    fetchScatterChartData();
    updateChart();
  }, []);
  useEffect(() => {
    checkImageExists(false);
  }, [fileName]);

  const percentileValue = useMemo(() => {
    if (!scatterData || scatterData.length === 0) return 0;

    let activePoint = formData.l_measurement;

    const sortedValues = scatterData.map((point) => point[1]).sort((a, b) => a - b);
    const belowCount = sortedValues.filter((value) => value < activePoint).length;
    const percentile = (belowCount / sortedValues.length) * 100;

    return Number(percentile.toFixed(2));
  }, [formData, scatterData, measurementType]);

  const checkImageExists = async (isImageExist = true) => {
    // const today = new Date();
    const datedFileName = getFileName(blurbData, hashArray, initialPathname, null, locale);
    const baseUrl = process.env.NEXT_PUBLIC_CHART_IMAGE_URL || '';
    const [beforeUpload, afterUpload] = baseUrl.split('/upload');
    const segment = (afterUpload || '').replace(/^\/|\/$/g, '');
    const imageUrl = segment
      ? `${beforeUpload}/upload/${segment}/${datedFileName}.png`
      : `${beforeUpload}/upload/${datedFileName}.png`;

    const response = await fetch(imageUrl, { method: 'HEAD' });
    if (response.ok) {
      setChartImage(imageUrl);
    } else {
      if (!isImageExist) {
        setGenerateNewImage(true);
      }
    }
  };

  useEffect(() => {
    seo(safeWindow.location.pathname);
    setHashArray(initialPathname.split('/'));
  }, [id]);

  const isLengthChart = useMemo(() => !initialPathname?.includes('year'), [initialPathname]);

  useEffect(() => {
    if (generateNewImage) {
      newChart(false, false);
    }
  }, [chartImage, generateNewImage, isChartShow]);

  useEffect(() => {
    const isServerImage =
      typeof chartImage === 'string' &&
      chartImage.startsWith('https://res.cloudinary.com/') &&
      chartImage.includes('/upload/') &&
      chartImage.endsWith('.png');

    if (isChartShow && isNewChart && !isServerImage) {
      setTimeout(() => {
        captureChartAsImage();
      }, 3200);
    }
  }, [isChartShow, isNewChart, chartImage]);
  function stripUOMFromPath(path, validUnits) {
    const segments = path.split('/').filter(Boolean);
    const last = segments.at(-1)?.toLowerCase();
    return validUnits.includes(last) ? '/' + segments.slice(0, -1).join('/') : '/' + segments.join('/');
  }

  useEffect(() => {
    const ldJsonData = getJSON_LD_infoURL(initialPathname);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'QuantitativeValue',
      name: `Average ${ldJsonData.chartType}`,
      description: `Average ${ldJsonData.chartType} for ${ldJsonData.gender.toLocaleLowerCase()} ${
        blurbData?.ageDesc
      } `,
      value: _ceil(blurbData?.percentile_50),
      unitCode: formData.unit.toUpperCase(),
      valueReference: [
        {
          '@type': 'PropertyValue',
          name: 'Age',
          value: ldJsonData.age,
          unitText: ldJsonData.ageUnit,
          description: blurbData?.ageDesc,
        },
        {
          '@type': 'PropertyValue',
          name: 'Gender',
          value: ldJsonData.gender,
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [blurbData?.ageDesc]);

  // Check for anonymous session on component mount
  useEffect(() => {
    if (!session.doesSessionExist) {
      const sessionId = getAnonymousSessionId();
      if (sessionId) {
        setAnonymousSessionId(sessionId);
      }
    }
  }, [session.doesSessionExist]);
  // ---------- helpers ----------

  function computeSafeBounds(minVal, maxVal, p50Val) {
    minVal = Number(minVal) || 0;
    maxVal = Number(maxVal) || 0;
    p50Val = Number(p50Val) || 0;

    if (!maxVal || maxVal <= 0) {
      maxVal = Math.max(1, p50Val * 10, 1_000_000);
    }

    if (!minVal || minVal <= 0) {
      minVal = Math.max(1, p50Val * 0.01);
    }

    if (!p50Val || p50Val <= 0) {
      p50Val = Math.exp((Math.log(Math.max(1, minVal)) + Math.log(maxVal)) / 2);
    }

    if (minVal >= p50Val) minVal = Math.max(1, p50Val * 0.5);
    if (maxVal <= p50Val) maxVal = Math.max(p50Val * 2, p50Val + 1);

    // bump the max 10–20% beyond the provided bound
    maxVal = maxVal * 1.08;

    return { minVal, maxVal, p50Val };
  }

  // finer, adaptive increments for small numbers (prevents lower collapse)
  function chooseIncrement(value) {
    if (value < 100) return 5; // fine grain under 100
    if (value < 500) return 10;
    if (value < 1000) return 25;
    if (value < 5000) return 100;
    if (value < 10000) return 500;
    if (value < 100000) return 5000;
    if (value < 1000000) return 50000;
    return 1000000;
  }

  function sliderToUSD(sliderVal, rawMin, rawMax, rawP50) {
    // compute safe bounds first
    const { minVal, maxVal, p50Val } = computeSafeBounds(rawMin, rawMax, rawP50);

    const logMin = Math.log10(minVal);
    const logMax = Math.log10(maxVal);
    const logP50 = Math.log10(p50Val);

    const ratio = Math.max(0, Math.min(1, sliderVal / 100));

    // anchored mapping: 0..50 -> min..p50, 50..100 -> p50..max (linear in log-space)
    let logValue;
    if (ratio <= 0.5) {
      const t = ratio / 0.5; // 0..1 between min and p50
      logValue = logMin + t * (logP50 - logMin);
    } else {
      const t = (ratio - 0.5) / 0.5; // 0..1 between p50 and max
      logValue = logP50 + t * (logMax - logP50);
    }

    let rawValue = Math.pow(10, logValue);

    // clamp into the bounds (prevents rounding below min)
    rawValue = Math.min(Math.max(rawValue, minVal), maxVal);

    // rounding with adaptive increments (finer at small values)
    const inc = chooseIncrement(rawValue);
    let rounded = Math.round(rawValue / inc) * inc;

    // ensure rounding doesn't fall outside [minVal, maxVal]
    if (rounded < minVal) rounded = Math.ceil(minVal / inc) * inc;
    if (rounded > maxVal) rounded = Math.floor(maxVal / inc) * inc;

    return rounded;
  }

  function usdToSlider(usdValue, rawMin, rawMax, rawP50) {
    const { minVal, maxVal, p50Val } = computeSafeBounds(rawMin, rawMax, rawP50);

    // clamp
    let value = Math.min(Math.max(usdValue, minVal), maxVal);

    const logMin = Math.log10(minVal);
    const logMax = Math.log10(maxVal);
    const logP50 = Math.log10(p50Val);
    const logValue = Math.log10(value);

    let ratio;
    if (logValue <= logP50) {
      // in lower half (min → p50)
      ratio = ((logValue - logMin) / (logP50 - logMin)) * 0.5;
    } else {
      // in upper half (p50 → max)
      ratio = 0.5 + ((logValue - logP50) / (logMax - logP50)) * 0.5;
    }

    return Math.round(ratio * 100); // slider steps (0–100)
  }

  const [sliderIncrementStates, setSliderIncrementStates] = useState({
    rawMin: 0,
    rawMax: 0,
    p50Val: 0,
  });
  // derive initial slider position (do this once on mount or whenever bounds/p50 change)
  const initialSlider = usdToSlider(
    sliderIncrementStates.p50Val,
    sliderIncrementStates.rawMin,
    sliderIncrementStates.rawMax,
    sliderIncrementStates.p50Val,
  );

  useEffect(() => {
    setFormData((prev) => {
      if (typeof prev.sliderValue === 'number') return prev;
      return { ...prev, sliderValue: initialSlider };
    });
  }, [initialSlider]);

  useEffect(() => {
    const rawMin = Number(
      measurementConfig.min?.(
        formData.unit,
        isLengthChart,
        calculated_age_slider,
        percentileData.floorData,
        percentileData.ceilData,
      ),
    );

    const rawMax = Number(
      measurementConfig.maxValue?.(
        formData.unit,
        isLengthChart,
        calculated_age_slider,
        percentileData.floorData,
        percentileData.ceilData,
      ),
    );

    // use l_measurement or fallback
    const p50Val = Number(metaInfo.defaultMeasurementValue ?? 0);

    setSliderIncrementStates({ rawMin, rawMax, p50Val });
  }, [percentileData]);

  // TODO // CODE TO REMOVE ABOVE
  const sliderMaxUSD = sliderToUSD(
    100,
    sliderIncrementStates.rawMin,
    sliderIncrementStates.rawMax,
    sliderIncrementStates.p50Val,
  );
  let calculated_age = _ceil(calculateAgeInMonthsWithTwoDates(formData.dob, formData.md));
  let url = formulateChartUrl(formData, chartLinkNmae, calculated_age, params.unit);
  console.log(safeWindow.location.pathname === url, 'check urls');

  return (
    <div className="chart-container">
      <SaveModal
        isOpen={showSaveModal}
        onHide={handleCloseSaveModal}
        saveMeasurementObj={result}
        formData={formData}
        familyMembersList={familyMembersList}
      />
      <ChartSpeedDial chartRef={chart} saveMeasurementObj={result} chartExportRef={chartExportRef} />
      <Modal centered isOpen={showFmModal} onHide={handleClose}>
        <FamilyMemberForm closeBtn={true} handleClose={handleClose} />
      </Modal>
      <section className="chart-top px-4 wrapper-md bg-white">
        {children}
        <div ref={uomRef}></div>

        {blurbData?.ageDesc && isChartShow && showClientComponent ? (
          <div className="row mt-4">
            <div className="col">
              <IdleCalulateText
                chartImageAlt={chartImageAlt}
                chartImage={chartImage}
                blurbData={blurbData || false}
                unit={formData?.unit}
                hashArray={hashArray}
                // hashArray={['check', 'check 2', 'check 3']}
                urlUnit={params.unit}
                metaInfo={metaInfo}
                pathname={safeWindow.location.pathname}
                ageInMonths={calculated_age_slider}
              />
            </div>
          </div>
        ) : null}
      </section>
      <section className="plotting p-4 chart-form-wrapper bg-white">
        <div className="container max-width">
          <div className="row plotting-form-container" style={{ justifyContent: 'center' }}>
            <div className="col col-md-8">
              <form className="plotting-form bs-example form-horizontal">
                {session.doesSessionExist && (
                  <div className="row py-1">
                    <div className="col-12 sub-title-chart mb-3">
                      <span>{t('form.letsMeasure')}</span>
                    </div>
                    <div className="col-10 ps-0 pe-2">
                      <select
                        id="member"
                        FamilyMemberForm
                        name="member_measurement"
                        value={safeWindow.sessionStorage.getItem('member_id')}
                        className="form-select form-control"
                        onChange={handleFamilyMemberChange}
                      >
                        <option key={false} value={null}>
                          {t('baseChart.selectFamilyMember')}
                        </option>
                        {familyMembersList?.map((el) => (
                          <option key={el.id} value={el.id}>
                            {el.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-1 d-flex flex-direction-coloumn justify-content-center align-items-center ms-1">
                      <button onClick={handleAddFamButton} className="add-family-member-link">
                        <img className="img-fluid" src="/assets/images/icons/addfamily.svg" alt="addfamily" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="row py-1 gender-wrapper">
                  <RadioGroup pathname={hashArray} gender={formData.gender} shouldRedirect={true} />
                  <div className="col-12 sub-title-chart mb-3 text-start" style={{ padding: '3px 25px 0 0' }}>
                    <span>{t('form.gender')}</span>
                  </div>
                  <div className="col-12 radio px-0">
                    <GenderSelect
                      formData={formData}
                      hashArray={hashArray}
                      onChange={handleInputChange}
                      ageInMonths={calculated_age_slider}
                      isDisabled={hashArray[2] === 'household' ? true : false}
                    />
                  </div>
                </div>

                <div className="py-1 dob-container ">
                  <div className="sub-dop-container px-0 pe-2  ">
                    <div className="sub-title-chart mb-3">
                      <span>{t('form.dateOfBirth')}</span>
                    </div>
                    <div className="col">
                      <div className="input-wrapper">
                        <StyledDatePicker
                          name="dob"
                          id="dob"
                          className={`dob text-dark form-control ${
                            validations && 'error-border'
                          } bg-content-orange dop-input`}
                          value={formData.dob ? dayjs(formData.dob) : null}
                          onChange={handleDateChange}
                          iconTop={'50%'}
                          minHeight={'100%'}
                          padding={'0px'}
                          iconColor={'#7C5832'}
                        />
                      </div>
                      <span className="color-light-brown mt-1">{t('baseChart.dateHint')}</span>
                    </div>

                    {validations && <span className="error-text">{validations}</span>}
                  </div>

                  <div className="sub-dop-container px-0 pe-2 ">
                    <div className="sub-title-chart mb-3">
                      <span>{getChartType(hashArray, locale)}</span>
                    </div>
                    <div className="col">
                      <div className="input-wrapper">
                        <input
                          name="l_measurement"
                          type="text"
                          inputmode="numeric"
                          min={sliderIncrementStates.rawMin}
                          max={sliderMaxUSD}
                          value={formData.l_measurement ? Number(formData.l_measurement).toLocaleString() : ''}
                          className="dob text-dark bg-content-orange dop-input"
                          style={{ minHeight: 45 }}
                          onChange={(e) => {
                            // Allow only digits and commas, strip everything else
                            let cleaned = e.target.value.replace(/[^\d]/g, '');

                            // Parse into number
                            let rawValue = Number(cleaned);

                            const min = sliderIncrementStates.rawMin;
                            const max = sliderMaxUSD;

                            // Clamp
                            if (rawValue < min) rawValue = min;
                            if (rawValue > max) rawValue = max;

                            // Update formData (store raw number)
                            handleInputChange({
                              target: { name: e.target.name, value: rawValue },
                            });

                            // Update slider
                            const sliderVal = usdToSlider(
                              rawValue,
                              sliderIncrementStates.rawMin,
                              sliderIncrementStates.rawMax,
                              sliderIncrementStates.p50Val,
                            );
                            changeSliderValue(sliderVal);
                          }}
                        />

                        <span className="color-light-brown mr-2" style={{ marginRight: '6px' }}>
                          {params.unit ? params.unit : 'USD'}
                        </span>
                      </div>
                      <span className="color-light-brown mt-1">
                        {t('baseChart.enterOr')}{' '}
                        <Link href={`/calculators/${hashArray[1]}-calculator`} prefetch={false}>
                          <span className="text-content-green" style={{ fontWeight: '600' }}>
                            {t('baseChart.calculateHere', { chartType: getChartType(hashArray, locale) })}
                          </span>
                        </Link>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col py-1 section-2 justify-content-center">
                  <div
                    className={
                      'col-12 col-lg-6 px-0 pe-2 position-relative i_measurement-wrapper d-flex uom-slider-wrapper justify-content-between'
                    }
                  >
                    <div className="sub-title-chart d-flex ">
                      <span>{localizedChartType}</span>
                      <div className="ms-3">({formatCurrency(Number(formData.l_measurement) || 0, locale)})</div>
                    </div>
                    <>
                      <RangeSlider
                        aria-label={t('baseChart.alwaysVisible')}
                        name="l_measurement"
                        valueLabelDisplay="on"
                        required
                        value={formData.sliderValue}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelFormat={(val) => {
                          return formatCurrency(sliderToUSD(
                            val,
                            sliderIncrementStates.rawMin,
                            sliderIncrementStates.rawMax,
                            sliderIncrementStates.p50Val,
                          ), locale);
                        }}
                        onChange={(e, newValue) => {
                          const usd = sliderToUSD(
                            newValue,
                            sliderIncrementStates.rawMin,
                            sliderIncrementStates.rawMax,
                            sliderIncrementStates.p50Val,
                          );
                          handleInputChange({ target: { name: 'l_measurement', value: usd } }, usd);
                          changeSliderValue(newValue); // update sliderValue
                        }}
                      />
                    </>
                  </div>
                  {/* <div
                    className={`col-12 col-md-6 px-0 unit-measure uom-units-wrapper d-flex justify-content-between 
                    ${measurementType === 'bmi' && 'uom-units-stacked-wrapper pt-3'}`}
                  >
                    <UomOptions
                      // onChange={handleUnitChange}
                      {...unitOptions}
                      unit={formData.unit}
                      initialPathname={initialPathname}
                    />
                  </div> */}
                </div>
                <div className="row py-4 bottom-section">
                  <div className="col d-flex justify-content-center">
                    <button
                      type="button"
                      className={`btn btn-primary ${!viewChartDisable && !isDefault ? 'viewChartChange' : ''}
                      `}
                      onClick={newChart}
                      //className ${isValueChange ? 'change' : ''}
                      disabled={viewChartDisable}
                      ref={chartScrollRef}
                    >
                      <span className="down-arrow">{t('baseChart.displayOnChart')}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      <div
        className="results-section p-4 chart-summary-wrapper scatter-chart-wrapper mt-4 mb-4"
        id={`${measurementType}-chart-container`}
        ref={chartRef}
      >
        {isChartShow && (
          <>
            <div className="row py-4">
              <md-card>
                <md-card-content>
                  <div
                    id={`${measurementType}_chart`}
                    className={`max-width ${Object.keys(chartOpt).length !== 0 ? 'd-block' : 'd-none'}`}
                  >
                    <h2 className="dark-brown-color">{t('baseChart.yourResults')}</h2>
                    {measurementType ? (
                      <p className="text-center green-color">
                        {t('baseChart.scfComparison', {
                          chartType: getChartType(hashArray, locale),
                          higherPct: result?.percentile.toFixed(1),
                          lowerPct: (100 - result?.percentile).toFixed(1),
                          gender: hashArray[2],
                        })}
                      </p>
                    ) : null}
                    {chartOpt && (
                      <ChartComponent
                        chartRef={chartRef}
                        formData={formData}
                        chart={chart}
                        setZoomedIn={setZoomedIn}
                        zoomedIn={zoomedIn}
                        chartOpt={chartOpt}
                        chartExportRef={chartExportRef}
                        setChart={setChart}
                        id={`${measurementType}_chart`}
                        className={`${measurementType}_chart`}
                        activePercentile={result?.percentile}
                      />
                    )}
                  </div>
                  <Skeleton
                    className={`skeleton-bg ${Object.keys(chartOpt).length !== 0 ? 'd-none' : 'd-block'}`}
                    animation="wave"
                    variant="rectangular"
                    width={'100%'}
                    height={400}
                  />
                </md-card-content>
              </md-card>
            </div>
            <div class="eclispe chart-eclipse-right charteclipse-top"></div>
            <div class="eclispe chart-eclipse-left chart-eclipse-top"></div>
          </>
        )}

        <section
          className={`results-section py-4 chart-summary-wrapper scatter-chart-wrapper mt-4 mb-4 ${
            isChartShow ? 'divder-green' : ''
          }`}
        >
          {isChartShow && (
            <div className="row text-center">
              <div className="col-12 col-md-8 col-lg-7 mt-3 mt-lg-4 results-values percentiels-values max-width">
                <div className="d-flex justify-content-between my-md-2">
                  <h6 className="green-color">{t('baseChart.percentile')}</h6> <span>{result.percentile}%</span>
                </div>
                <div className="d-flex justify-content-between my-md-2">
                  <h6 className="green-color">{t('baseChart.age')}</h6> <span>{getAge(formData.dob, formData.md, t)}</span>
                </div>
                {measurementConfig?.getResultDisplay(formData, t, locale)}
              </div>
              {!session.doesSessionExist && anonymousSessionId && (
                <div className="pt-3">
                  <div className="col-12 green-color">
                    {/* <div className="alert alert-info"> */}
                    <small>{t('baseChart.savedAnonymously')}</small>
                    {/* </div> */}
                  </div>
                </div>
              )}
              <div className="col-12 pt-3 mb-1 mb-md-3">
                <button
                  className="btn btn-primary m-auto d-block view-chart-btn change"
                  onClick={handleShowSaveModal}
                  style={{ width: '150px' }}
                  disabled={!viewChartDisable}
                >
                  {t('baseChart.saveMeasurement')}
                </button>
              </div>
            </div>
          )}
          <div className="row ">
            <md-card>
              <md-card-content>
                <div
                  id={`${measurementType}_chart`}
                  className={`max-width ${Object.keys(scatterChartOpt).length !== 0 ? 'd-block' : 'd-none'}`}
                >
                  {isChartShow && (
                    <div className="text-center">
                      {session.doesSessionExist ? (
                        <p className="green-color">
                          {t('baseChart.saveToProfile', {
                            name: userProfile?.name || userProfile?.email?.split('@')[0],
                            measurementType,
                          })}
                        </p>
                      ) : (
                        <p className="green-color">
                          {anonymousSessionId
                            ? t('baseChart.signUpToAccess')
                            : t('baseChart.saveAndTrack')}
                        </p>
                      )}
                      <h2 className="dark-brown-color mt-5 mb-3">{t('baseChart.seeHowYouCompare')}</h2>
                      <p className="green-color">{isChartShow ? subtitle : null}</p>
                    </div>
                  )}
                  {!isChartShow && (
                    <div className="text-center">
                      <h2 className="dark-brown-color">{t('baseChart.allResults')}</h2>
                      <p className="green-color">
                        {t('baseChart.enterToCompare', { measurementType: localizedChartType })}
                      </p>
                    </div>
                  )}
                  {isChartShow &&
                    (measurementType ? (
                      <p className="text-center green-color">
                        {t('baseChart.nwComparison', {
                          chartType: getChartType(hashArray, locale),
                          morePct: percentileValue,
                          lessPct: 100 - percentileValue,
                          gender: hashArray[2],
                        })}
                      </p>
                    ) : null)}
                  {scatterChartOpt && (
                    <ChartComponent
                      chartRef={chartRef}
                      formData={formData}
                      chart={chart}
                      setZoomedIn={setZoomedIn}
                      zoomedIn={zoomedIn}
                      chartOpt={scatterChartOpt}
                      chartExportRef={chartExportRef}
                      setChart={setChart}
                      id={`${measurementType}_chart`}
                      className={`${measurementType}_chart`}
                      activePercentile={result?.percentile}
                    />
                  )}
                  <div className="text-center">
                    <p>
                      {t('baseChart.recordedCount', {
                        count: scatterData?.length ?? 0,
                        chartType: getChartType(hashArray, locale),
                        ageDesc: getAgeDescAdjective(params.id, locale),
                        gender: hashArray[2],
                        b: (chunks) => <b key="b">{chunks}</b>,
                      })}
                    </p>
                    <p className="green-color">{t('baseChart.updatesDaily')}</p>
                  </div>
                </div>
                <Skeleton
                  className={`skeleton-bg ${Object.keys(scatterChartOpt).length !== 0 ? 'd-none' : 'd-block'}`}
                  animation="wave"
                  variant="rectangular"
                  width={'100%'}
                  height={400}
                />
              </md-card-content>
            </md-card>
          </div>
        </section>
      </div>
      <section className="chart-next-measurement-wrapper">
        <div className="row">
          <div className="col-12 m-auto pb-4 text-center">
            <p>
              <b>
                {t('baseChart.otherMeasurements', {
                  ageDesc: getAgeDescAdjective(params.id, locale),
                  gender: hashArray[2],
                })}
              </b>
            </p>

            {/* === FINANCIAL COMPARISONS === */}
            <p>{t('baseChart.financialComparisons')}</p>
            <nav aria-label={t('baseChart.financialComparisons')}>
              <ul
                className="d-md-flex justify-content-center flex-wrap list-unstyled mb-3"
                style={{ listStyle: 'none' }}
              >
                {Object.entries(otherLinks).map(([key, rawUrl]) => {
                  if (!rawUrl) return null;

                  // Remove trailing slash if present, but ignore UOM logic entirely
                  const cleanUrl = rawUrl.endsWith('/') ? rawUrl.slice(0, -1) : rawUrl;

                  // Get current path (no UOM filtering)
                  const cleanCurrentPath = safeWindow.location.pathname.replace(/\/$/, '');

                  // Only show if it's not the current chart
                  if (cleanUrl === cleanCurrentPath) return null;

                  return (
                    <li key={key} className="mx-1">
                      <Link prefetch={false} href={cleanUrl} rel="noopener noreferrer" className="next-m-btn my-2">
                        <span className="ms-2 d-inline-block">{t(OTHER_LINK_CHART_TYPE_KEY[key] || 'chartType.netWorth')}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* === BODY COMPARISONS === */}
            <p>{t('baseChart.bodyComparisons')}</p>
            <nav aria-label={t('baseChart.bodyComparisons')}>
              <ul className="d-md-flex justify-content-center flex-wrap list-unstyled" style={{ listStyle: 'none' }}>
                <li className="mx-1">
                  <Link
                    prefetch={false}
                    href={`https://www.lifemeasure.com/weight-charts/${
                      hashArray[2] === 'men' && calculated_age_slider < 240
                        ? 'boys'
                        : hashArray[2] === 'men' && calculated_age_slider >= 240
                        ? 'men'
                        : hashArray[2] === 'women' && calculated_age_slider < 240
                        ? 'girls'
                        : 'women'
                    }/${hashArray[3]}`}
                    target="_blank"
                    rel="noopener"
                    className="next-m-btn my-2"
                  >
                    <span className="ms-2 d-inline-block">{t('baseChart.linkWeight')}</span>
                  </Link>
                </li>

                <li className="mx-1">
                  <Link
                    prefetch={false}
                    href={`https://www.lifemeasure.com/bmi-charts/${
                      hashArray[2] === 'men' && calculated_age_slider < 240
                        ? 'boys'
                        : hashArray[2] === 'men' && calculated_age_slider >= 240
                        ? 'men'
                        : hashArray[2] === 'women' && calculated_age_slider < 240
                        ? 'girls'
                        : 'women'
                    }/${hashArray[3]}`}
                    target="_blank"
                    rel="noopener"
                    className="next-m-btn my-2"
                  >
                    <span className="ms-2 d-inline-block">{t('baseChart.linkBmi')}</span>
                  </Link>
                </li>

                <li className="mx-1">
                  <Link
                    prefetch={false}
                    href={`https://www.lifemeasure.com/height-charts/${
                      hashArray[2] === 'men' && calculated_age_slider < 240
                        ? 'boys'
                        : hashArray[2] === 'men' && calculated_age_slider >= 240
                        ? 'men'
                        : hashArray[2] === 'women' && calculated_age_slider < 240
                        ? 'girls'
                        : 'women'
                    }/${hashArray[3]}`}
                    target="_blank"
                    rel="noopener"
                    className="next-m-btn my-2"
                  >
                    <span className="ms-2 d-inline-block">{t('baseChart.linkHeight')}</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>
    </div>
  );
};

export default React.memo(BaseChart);
